/**
 * The IANA timezone name the backend is told the user is in.
 *
 * Evisort stores no timezone preference for a user, so the browser is the only
 * source, and it is read fresh on every request: a value captured once freezes
 * at boot and stops being true as soon as the user travels or the clock crosses
 * a DST boundary.
 *
 * Two transports send this and both must agree. The axios interceptor in
 * `createService` covers every REST call, and `createSseConnection` covers the
 * streaming chat, which does not go through axios. Sharing one function is what
 * stops them drifting into reporting different zones for the same user.
 */
export const CALLER_TIMEZONE_HEADER = 'User-Timezone';

/**
 * The browser's IANA zone name, or an empty string if it reports none.
 *
 * There is deliberately no default zone here. A guessed zone is indistinguishable
 * to the reader from a real one: the AI agents answer "what time is it" and
 * "due in 30 days" from this value, so naming a zone the user is not in produces
 * a wrong date stated with full confidence and nothing to indicate it was
 * invented.
 *
 * Per ECMA-402 a conforming browser always resolves a zone, so the empty case is
 * a safeguard rather than a path with real traffic.
 */
export const getCallerTimezone = (): string =>
  new Intl.DateTimeFormat().resolvedOptions().timeZone || '';

/**
 * The timezone header to merge into a request, which is nothing at all when the
 * browser reports no zone.
 *
 * Omitted rather than defaulted, because "I don't know" and "UTC" are different
 * facts and only the backend can act on the difference. Sent no zone, hermes
 * works in UTC and the agents say so, and are told to ask which zone the user
 * wants rather than assuming one. Sending `UTC` instead would throw that away:
 * it asserts the user is in UTC, which is a guess wearing the clothes of an
 * answer.
 */
export const callerTimezoneHeader = (): Record<string, string> => {
  const timezone = getCallerTimezone();
  return timezone ? { [CALLER_TIMEZONE_HEADER]: timezone } : {};
};
